import { useState } from "react";

import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { Trans, useTranslation } from "@/translations";

export function CodeOfConductDeletionModal({
  isOpened,
  isLoading,
  onOpenChange,
  onDelete,
}: {
  isOpened: boolean;
  isLoading?: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onDelete: () => void;
}): React.ReactNode {
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { t } = useTranslation();

  const codeOfConduct = useSessionUser().codeOfConduct.url;

  return (
    <ConfirmModal
      tone="error"
      title={t("component.community-post.delete-modal.title")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      onReject={() => onOpenChange(false)}
      onResolve={onDelete}
      isLoading={isLoading ?? false}
      resolveBtnProps={{
        disabled: !isConfirmed,
        text: t("component.community-post.delete-modal.confirm"),
        "data-testid": "modal-confirm-delete",
      }}
    >
      <Stack gap="md">
        <Notice
          variant="warning"
          icon={<AlertTriangleIcon />}
          message={t("component.community-post.delete-modal.notice")}
        />
        <Stack gap="md">
          <section>
            <h4 className="text-body-emphasis">{t("component.community-post.delete-modal.step-1")}</h4>
            <p>
              <Trans
                i18nKey="component.community-post.delete-modal.step-1-description"
                components={{
                  link: (content) => (
                    <Button variant="ghostPrimary" type="link" isExternal href={codeOfConduct}>
                      {content}
                    </Button>
                  ),
                }}
              />
            </p>
          </section>
          <section>
            <h4 className="text-body-emphasis">{t("component.community-post.delete-modal.step-2")}</h4>
            <p>
              <Trans
                i18nKey="component.community-post.delete-modal.step-2-description"
                components={{
                  link: (content) => (
                    <Button variant="ghostPrimary" type="link" isExternal href={codeOfConduct}>
                      {content}
                    </Button>
                  ),
                }}
              />
            </p>
          </section>
          <section>
            <h4 className="text-body-emphasis">{t("component.community-post.delete-modal.step-3")}</h4>
            <p>
              <Trans
                i18nKey="component.community-post.delete-modal.step-3-description"
                components={{
                  link: (content) => (
                    <Button variant="ghostPrimary" type="link" isExternal href={codeOfConduct}>
                      {content}
                    </Button>
                  ),
                }}
              />
            </p>
          </section>
        </Stack>

        <Checkbox
          data-testid="checkbox-confirm-delete"
          id="checkbox-confirm"
          name="isConfirmed"
          checked={isConfirmed}
          onChange={(e) => setIsConfirmed(e.target.checked)}
          label={t("component.community-post.delete-modal.confirm-checkbox")}
        />
      </Stack>
    </ConfirmModal>
  );
}
